import React, { useState, useEffect } from 'react';
import { supabase, getLessonsTable, getWordsTable } from '../../utils/supabase';
import { type Lesson, type Word, type LanguageLevel } from '../../types';
import { LessonManagement } from './LessonManagement';

interface LessonAdminPanelProps {
  activeLevel: LanguageLevel;
  language: string;
}

export function LessonAdminPanel({ activeLevel, language }: LessonAdminPanelProps) {
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if current user is admin
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('is_admin')
            .eq('user_id', user.id)
            .single();
          
          setIsAdmin(!!profile?.is_admin);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };
    
    checkAdminStatus();
    loadData();
  }, [activeLevel, language]);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('Set adminLanguage to:', language);

      // Fetch all lessons for the current level
      const table = getLessonsTable(language);
      console.log('Loading data from table:', table, 'for language:', language);

      const { data: lessonsData, error: lessonsError } = await supabase
        .from(table)
        .select(`
          id,
          level,
          title,
          description,
          text,
          text_translation,
          category,
          difficulty,
          is_review,
          show_text,
          audio_url,
          segments_timepoints,
          order_index,
          language,
          created_at,
          updated_at
        `)
        .eq('is_review', false)
        .eq('level', activeLevel)
        .eq('language', language)
        .order('order_index', { ascending: true })
        .order('created_at', { ascending: true });

      if (lessonsError) {
        console.error('Error loading lessons:', lessonsError);
        throw lessonsError;
      }

      // Fetch words for all lessons in a single query
      const wordsTable = getWordsTable(language);
      console.log('Loading words from table:', wordsTable);

      const { data: wordsData, error: wordsError } = await supabase
        .from(wordsTable)
        .select(`
          id,
          lesson_id,
          dutch,
          translation,
          pronunciation,
          example,
          example_translation
        `)
        .in('lesson_id', lessonsData?.map(lesson => lesson.id) || []);

      if (wordsError) {
        console.error('Error loading words:', wordsError);
        throw wordsError;
      }

      // Combine lessons with their words
      const lessonsWithWords = lessonsData?.map(lesson => ({
        ...lesson,
        words: (wordsData || []).filter(word => word.lesson_id === lesson.id)
      })) || [];

      setLessons(lessonsWithWords);

    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLesson = async (lessonData: Partial<Lesson>) => {
    try {
      console.log('Saving lesson:', lessonData.title);
      
      // Verify admin status before proceeding
      if (!isAdmin) {
        throw new Error('You do not have admin privileges to save lessons');
      }

      const timestamp = new Date().toISOString();
      const table = getLessonsTable();
      
      // Validate required fields
      if (!lessonData.title) {
        throw new Error('Lesson title is required');
      }
      
      if (!lessonData.level) {
        throw new Error('Lesson level is required');
      }

      let orderIndex = lessonData.order_index;
      let savedLessonId = lessonData.id;

      // Prepare the lesson data
      const lessonPayload = {
        language,
        level: lessonData.level || activeLevel,
        title: lessonData.title,
        description: lessonData.description,
        text: lessonData.text,
        text_translation: lessonData.text_translation,
        category: lessonData.category,
        difficulty: lessonData.difficulty,
        show_text: lessonData.show_text !== false,
        audio_url: lessonData.audio_url || null,
        segments_timepoints: lessonData.segments_timepoints,
        order_index: orderIndex || 0,
        updated_at: timestamp,
        is_review: false
      };
      
      // Check if parsing from text with both English and Persian fields
      // We need to save to BOTH tables simultaneously
      if (lessonData.parsed_persian_data) {
        const hasEnglishData = lessonData.parsed_persian_data.title && lessonData.parsed_persian_data.translation;
        const hasPersianData = lessonData.title && lessonData.text_translation;

        // Prepare English lesson payload (for lesson_content table)
        const englishPayload = {
          language: 'English',
          level: lessonData.level || activeLevel,
          title: language === 'English' ? lessonData.title : lessonData.parsed_persian_data.title,
          description: language === 'English' ? lessonData.description : lessonData.parsed_persian_data.description,
          text: lessonData.text, // Same Dutch text for both
          text_translation: language === 'English' ? lessonData.text_translation : lessonData.parsed_persian_data.translation,
          category: lessonData.category,
          difficulty: lessonData.difficulty,
          show_text: lessonData.show_text !== false,
          audio_url: lessonData.audio_url || null,
          segments_timepoints: lessonData.segments_timepoints,
          order_index: orderIndex || 0,
          updated_at: timestamp,
          is_review: false
        };

        // Prepare Persian lesson payload (for persian_lesson_content table)
        const persianPayload = {
          language: 'Persian',
          level: lessonData.level || activeLevel,
          title: language === 'Persian' ? lessonData.title : lessonData.parsed_persian_data.title,
          description: language === 'Persian' ? lessonData.description : lessonData.parsed_persian_data.description,
          text: lessonData.text, // Same Dutch text for both
          text_translation: language === 'Persian' ? lessonData.text_translation : lessonData.parsed_persian_data.translation,
          category: lessonData.category,
          difficulty: lessonData.difficulty,
          show_text: lessonData.show_text !== false,
          audio_url: lessonData.audio_url || null,
          segments_timepoints: lessonData.segments_timepoints,
          order_index: orderIndex || 0,
          updated_at: timestamp,
          is_review: false
        };

        // Save to lesson_content (English) table
        if (hasEnglishData) {
          const { data: existingEnglishLesson } = await supabase
            .from('lesson_content')
            .select('id')
            .eq('level', activeLevel)
            .eq('title', englishPayload.title)
            .eq('language', 'English')
            .maybeSingle();

          if (existingEnglishLesson) {
            const { error: englishError } = await supabase
              .from('lesson_content')
              .update(englishPayload)
              .eq('id', existingEnglishLesson.id);

            if (englishError) {
              console.error('Error updating English lesson:', englishError);
              throw englishError;
            }
            console.log('English lesson updated successfully');
          } else {
            const { error: englishError } = await supabase
              .from('lesson_content')
              .insert([englishPayload]);

            if (englishError) {
              console.error('Error creating English lesson:', englishError);
              throw englishError;
            }
            console.log('English lesson created successfully');
          }
        }

        // Save to persian_lesson_content table
        if (hasPersianData) {
          const { data: existingPersianLesson } = await supabase
            .from('persian_lesson_content')
            .select('id')
            .eq('level', activeLevel)
            .eq('title', persianPayload.title)
            .eq('language', 'Persian')
            .maybeSingle();

          if (existingPersianLesson) {
            const { error: persianError } = await supabase
              .from('persian_lesson_content')
              .update(persianPayload)
              .eq('id', existingPersianLesson.id);

            if (persianError) {
              console.error('Error updating Persian lesson:', persianError);
              throw persianError;
            }
            console.log('Persian lesson updated successfully');
          } else {
            const { error: persianError } = await supabase
              .from('persian_lesson_content')
              .insert([persianPayload]);

            if (persianError) {
              console.error('Error creating Persian lesson:', persianError);
              throw persianError;
            }
            console.log('Persian lesson created successfully');
          }
        }

        // Skip the normal save operation below since we've already saved to both tables
        await loadData();
        return;
      }

      // Normal save operation (when not using text parsing with dual translations)
      if (savedLessonId) {
        const { error } = await supabase
          .from(table)
          .update(lessonPayload)
          .eq('id', savedLessonId);

        if (error) throw error;
        console.log('Lesson updated successfully');
      } else {
        const { data: newLesson, error } = await supabase
          .from(table)
          .insert([lessonPayload])
          .select()
          .single();

        if (error) throw error;
        console.log('Lesson created successfully:', newLesson?.id);
        savedLessonId = newLesson.id;
      }

      await loadData();
    } catch (error) {
      console.error('Error saving lesson:', error);
      throw error;
    }
  };

  const handleDeleteLesson = async (lessonId: string) => {
    if (!window.confirm('Are you sure you want to delete this lesson? This will also delete all associated words and examples.')) {
      return;
    }

    try {
      // Verify admin status before proceeding
      if (!isAdmin) {
        throw new Error('You do not have admin privileges to delete lessons');
      }

      // Delete associated words first
      const { error: wordsError } = await supabase
        .from(getWordsTable())
        .delete()
        .eq('lesson_id', lessonId);

      if (wordsError) throw wordsError;

      // Then delete the lesson
      const { error: lessonError } = await supabase
        .from(getLessonsTable())
        .delete()
        .eq('id', lessonId);

      if (lessonError) throw lessonError;

      await loadData();
    } catch (error) {
      console.error('Error deleting lesson:', error);
      alert('Error deleting lesson. Please try again.');
    }
  };

  const handleSaveWord = async (wordData: Partial<Word>, lessonId: string) => {
    try {
      // Verify admin status before proceeding
      if (!isAdmin) {
        throw new Error('You do not have admin privileges to save words');
      }

      const wordsTable = getWordsTable();
      console.log('Saving word to table:', wordsTable);

      if (wordData.id) {
        const { error } = await supabase
          .from(wordsTable)
          .upsert({
            id: wordData.id,
            lesson_id: lessonId,
            dutch: wordData.dutch,
            translation: wordData.translation,
            pronunciation: wordData.pronunciation,
            example: wordData.example,
            example_translation: wordData.example_translation
          });

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from(wordsTable)
          .insert([{
            lesson_id: lessonId,
            dutch: wordData.dutch,
            translation: wordData.translation,
            pronunciation: wordData.pronunciation,
            example: wordData.example,
            example_translation: wordData.example_translation
          }]);

        if (error) throw error;
      }

      await loadData();
    } catch (error) {
      console.error('Error saving word:', error);
      throw error;
    }
  };

  const handleDeleteWord = async (wordId: string) => {
    if (!window.confirm('Are you sure you want to delete this word and all its examples?')) {
      return;
    }

    try {
      // Verify admin status before proceeding
      if (!isAdmin) {
        throw new Error('You do not have admin privileges to delete words');
      }

      const { error } = await supabase
        .from(getWordsTable())
        .delete()
        .eq('id', wordId);

      if (error) throw error;
      await loadData();
    } catch (error) {
      console.error('Error deleting word:', error);
      alert('Error deleting word. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold text-red-600">Access Denied</h2>
            <p className="mt-2">You do not have admin privileges to access this section.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <LessonManagement
          lessons={lessons}
          activeLevel={activeLevel}
          language={language}
          onSaveLesson={handleSaveLesson}
          onDeleteLesson={handleDeleteLesson}
          onSaveWord={handleSaveWord}
          onDeleteWord={handleDeleteWord}
        />
      </div>
    </div>
  );
}